import { ArrowLeft, Loader2, Pencil, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useServiceDetail,
  useStaffForService,
} from "@/hooks/useManageServices";
import { useClinics } from "@/hooks/useClinics";
import { ReadField } from "./shared/ReadField";

interface ServiceDetailViewProps {
  serviceId: string;
  onEdit: (serviceId: string) => void;
  onBack: () => void;
  canManageServices: boolean;
}

const SERVICE_TYPE_LABEL: Record<string, string> = {
  baseline: "Baseline",
  regular: "Regular",
  urgent: "Urgent",
  other: "Other",
};

const MODE_LABEL: Record<string, string> = {
  audio: "Audio",
  video: "Video",
};

const yesNo = (v?: boolean) => (v ? "Yes" : "No");

const formatLabel = (raw: string | null | undefined) =>
  raw ? raw.replace(/_/g, " ") : "";

export const ServiceDetailView = ({
  serviceId,
  onEdit,
  onBack,
  canManageServices,
}: ServiceDetailViewProps) => {
  const { data: service, isLoading, isError } = useServiceDetail(serviceId);
  const { data: staffMembers } = useStaffForService(canManageServices);
  const { data: clinics } = useClinics();

  const assignedStaff =
    staffMembers?.filter((s) => service?.staff_ids?.includes(s.id)) || [];
  const availableClinics =
    clinics?.filter((c) => service?.available_clinic_ids?.includes(c.id)) ||
    [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !service) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-2">
        <Layers className="h-10 w-10 mb-2 opacity-40" />
        <p className="z-body font-medium">Failed to load service details.</p>
        <Button variant="outline" className="mt-4" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Go Back
        </Button>
      </div>
    );
  }

  const mode = service.mode ?? service.mode_of_service ?? "";

  return (
    <div className="h-full flex flex-col min-h-0 gap-4">
      <Card className="max-w-[2000px] flex-1 flex flex-col min-h-0 overflow-visible">
        <CardHeader className="shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1 flex items-center gap-3">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onBack}
                aria-label="Back to Services"
                className="shrink-0"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted border border-border flex items-center justify-center shrink-0">
                {service.image ? (
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="z-body font-medium text-muted-foreground">
                    {service.name?.[0]?.toUpperCase()}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex flex-col gap-1">
                <div className="flex items-center gap-3 flex-wrap min-w-0">
                  <CardTitle className="z-h2 truncate">
                    {service.name}
                  </CardTitle>
                  <Badge variant="secondary">Viewing</Badge>
                </div>
                <p className="z-caption text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1">
                  <Badge
                    variant="secondary"
                    className="bg-primary/10 text-primary border-primary/20"
                  >
                    {service.category_name || "Uncategorized"}
                  </Badge>
                  {service.is_active === false && (
                    <Badge variant="destructive">Inactive</Badge>
                  )}
                  {service.visible === false && (
                    <Badge variant="outline">Hidden</Badge>
                  )}
                </p>
              </div>
            </div>
            {canManageServices && (
              <Button onClick={() => onEdit(service.id)}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit Service
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto pt-2 pb-6">
          <Tabs defaultValue="details">
            <TabsList>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="schedule">Schedule</TabsTrigger>
              <TabsTrigger value="pricing">Pricing &amp; Follow-up</TabsTrigger>
              <TabsTrigger value="staff">Staff &amp; Visibility</TabsTrigger>
            </TabsList>

            {/* ─── Details ─────────────────────────────────────────── */}
            <TabsContent value="details" className="mt-4">
              <Card>
                <CardContent className="pt-6 pb-5 space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
                    <ReadField label="Service Name" value={service.name} />
                    <ReadField
                      label="Department"
                      value={service.category_name || ""}
                    />
                    <ReadField
                      label="Service Type"
                      value={
                        SERVICE_TYPE_LABEL[service.type] || service.type || ""
                      }
                    />
                    <ReadField
                      label="Maximum Participants"
                      value={service.max_participants ?? ""}
                    />
                    <ReadField
                      label="Location"
                      value={service.location || ""}
                    />
                    <ReadField
                      label="Mode of Consultation"
                      value={MODE_LABEL[mode] || mode || ""}
                    />
                  </div>
                  <div className="pt-2">
                    <ReadField
                      label="In-Person Available"
                      value={yesNo(service.is_in_person_available)}
                    />
                  </div>
                  {service.is_in_person_available && (
                    <ReadField
                      label="Available Clinics"
                      value={availableClinics.map((c) => c.name).join(", ")}
                    />
                  )}
                  <ReadField
                    label="Description"
                    value={service.description || ""}
                    multiline
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* ─── Schedule ────────────────────────────────────────── */}
            <TabsContent value="schedule" className="mt-4">
              <Card>
                <CardContent className="pt-6 pb-5 space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
                    <ReadField
                      label="Service Duration"
                      value={service.event_duration ?? ""}
                      unit="mins"
                    />
                    <ReadField
                      label="Slot Duration"
                      value={service.slot_duration ?? ""}
                      unit="mins"
                    />
                    <ReadField
                      label="Start Buffer"
                      value={service.start_buffer ?? 0}
                      unit="mins"
                    />
                    <ReadField
                      label="End Buffer"
                      value={service.end_buffer ?? 0}
                      unit="mins"
                    />
                    <ReadField
                      label="Min Time to Start"
                      value={service.min_time_to_start ?? 0}
                      unit="mins"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ─── Pricing & Follow-up ─────────────────────────────── */}
            <TabsContent value="pricing" className="mt-4">
              <Card>
                <CardContent className="pt-6 pb-5 space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
                    <ReadField label="Currency" value="Indian Rupee (INR)" />
                    <ReadField
                      label="Amount"
                      value={service.cost ?? ""}
                      unit="₹"
                    />
                  </div>
                  <div className="pt-2">
                    <ReadField
                      label="Follow-up Available"
                      value={yesNo(service.is_followup_available)}
                    />
                  </div>
                  {service.is_followup_available && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4 pt-2">
                      <ReadField
                        label="Follow-up Limit Days"
                        value={service.followup_limit_days ?? 0}
                        unit="days"
                      />
                      <ReadField
                        label="Follow-up Duration"
                        value={service.followup_event_duration ?? 0}
                        unit="mins"
                      />
                      <ReadField
                        label="Follow-up Cost"
                        value={service.followup_cost ?? "0.00"}
                        unit="₹"
                      />
                      <ReadField
                        label="Follow-up Description"
                        value={service.followup_description || ""}
                        multiline
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ─── Staff & Visibility ──────────────────────────────── */}
            <TabsContent value="staff" className="mt-4">
              <Card>
                <CardContent className="pt-6 pb-5 space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
                    <ReadField
                      label="Active"
                      value={yesNo(service.is_active)}
                    />
                    <ReadField label="Visible" value={yesNo(service.visible)} />
                    <ReadField label="Order" value={service.order ?? 0} />
                  </div>

                  <div>
                    <p className="z-label text-muted-foreground mb-2">
                      Assigned Staff
                    </p>
                    {!canManageServices ? (
                      <p className="z-caption text-muted-foreground text-center py-6 border rounded-lg bg-muted/30">
                        Assigned staff details require service-management
                        access.
                      </p>
                    ) : assignedStaff.length > 0 ? (
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="z-eyebrow text-muted-foreground">
                                Name
                              </TableHead>
                              <TableHead className="z-eyebrow text-muted-foreground">
                                Role
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {assignedStaff.map((staff) => (
                              <TableRow key={staff.id}>
                                <TableCell className="z-body">
                                  <div className="flex items-center gap-2">
                                    <Avatar className="h-7 w-7">
                                      <AvatarImage
                                        src={staff.profile_pic || undefined}
                                      />
                                      <AvatarFallback className="bg-primary/10 text-primary z-caption">
                                        {staff.first_name?.[0]}
                                        {staff.last_name?.[0]}
                                      </AvatarFallback>
                                    </Avatar>
                                    <span>
                                      {staff.first_name} {staff.last_name}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-muted-foreground capitalize">
                                  {formatLabel(staff.user_type ?? "Staff") ||
                                    "Staff"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : (
                      <p className="z-caption text-muted-foreground text-center py-6 border rounded-lg bg-muted/30">
                        No staff members are currently assigned to this service.
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};
